"use client";

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, FormEvent } from 'react';
import { Search, ArrowUp, Paperclip, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { BorderTrail } from '@/components/ui/border-trail';

interface SearchBarProps {
  onSubmit?: (prompt: string) => void;
  isLoading?: boolean;
  initialValue?: string;
}

type SearchBarRef = {
  focus: () => void;
};

const SearchBar = forwardRef<SearchBarRef, SearchBarProps>(({ onSubmit, isLoading = false, initialValue = '' }, ref) => {
  const [prompt, setPrompt] = useState(initialValue);
  const [files, setFiles] = useState<File[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { toast } = useToast();
  const hasText = prompt.trim().length > 0;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  useImperativeHandle(ref, () => ({
    focus: () => {
      console.log("SearchBar: Manual focus triggered");
      if (textareaRef.current) {
        textareaRef.current.focus();
        console.log("SearchBar: Focus applied to textarea");
      } else {
        console.log("SearchBar: Failed to focus - textarea ref is null");
      }
    }
  }), []);

  useEffect(() => {
    if (initialValue) {
      console.log("SearchBar: initialValue changed, setting prompt", initialValue);
      setPrompt(initialValue);
      
      if (textareaRef.current) {
        console.log("SearchBar: Focusing after initialValue set");
        textareaRef.current.focus();
      } else {
        console.log("SearchBar: Failed to focus after initialValue - textarea ref is null");
      }
    }
  }, [initialValue]);

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    console.log("SearchBar: Form submit handler called");
    submitPrompt();
  };

  const submitPrompt = () => {
    if (!prompt.trim()) {
      console.log("SearchBar: Empty prompt, not submitting");
      return;
    }
    
    console.log("SearchBar: Submitting prompt:", prompt);
    
    localStorage.setItem('currentPrompt', prompt);
    localStorage.setItem('isGenerating', 'true');
    
    try {
      if (onSubmit) {
        console.log("SearchBar: Calling onSubmit handler");
        onSubmit(prompt);
      } else {
        console.log("SearchBar: No onSubmit handler, navigating to /builder");
        navigate('/builder');
      }
    } catch (error) {
      console.error("Error in submit handler:", error);
      toast({
        title: "Error",
        description: "Failed to process your request",
        variant: "destructive"
      });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  }

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    const uploadInput = document.getElementById('file-upload') as HTMLInputElement;
    if (uploadInput) {
      uploadInput.value = "";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      console.log("SearchBar: Ctrl+Enter pressed, submitting");
      e.preventDefault();
      submitPrompt();
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    console.log("SearchBar: Submit button clicked");
    e.preventDefault();
    submitPrompt();
  };
  
  return (
    <div className="w-full mx-auto transition-all relative z-10">
      <form 
        onSubmit={handleFormSubmit} 
        className="relative w-full overflow-hidden rounded-3xl"
      >
        <div className={cn(
          "relative backdrop-blur-md bg-gray-900/70 rounded-3xl transition-colors",
          hasText ? "border border-jade/40" : "border border-jade/20",
          isFocused ? "shadow-md shadow-jade/20" : ""
        )}>
          <div className="absolute inset-0 rounded-3xl overflow-hidden z-0 pointer-events-none">
            {(isFocused || hasText) && (
              <BorderTrail 
                className="bg-jade/60"
                size={80}
                transition={{
                  repeat: Infinity,
                  duration: 4,
                  ease: "linear",
                }}
                style={{ filter: 'blur(8px)' }}
              />
            )}
          </div>
          
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1 px-3 sm:px-4 pt-3 pb-1 relative z-20">
              {files.map((file, index) => (
                <div 
                  key={index} 
                  className="bg-jade/20 flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs text-white transition-all hover:bg-jade/30"
                >
                  <Paperclip className="size-3" />
                  <span className="max-w-[80px] sm:max-w-[100px] truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="hover:bg-jade/40 rounded-full p-0.5 transition-colors"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex items-start px-4 sm:px-5 py-3 sm:py-4 relative z-20">
            <Search className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-jade mt-2 mr-3 flex-shrink-0`} />
            <textarea 
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={isMobile ? "E-Mail erstellen..." : "Erstellen Sie eine professionelle E-Mail für Versicherungskunden..."}
              className="flex-grow py-1 text-base md:text-lg text-gray-100 bg-transparent border-none shadow-none placeholder-gray-400/80 resize-none min-h-[50px] sm:min-h-[60px] outline-none w-full font-medium"
              rows={isMobile ? 1 : 2}
            />
          </div>
          
          <div className="flex items-center justify-between px-4 sm:px-5 pb-3 sm:pb-4 relative z-20">
            <label 
              htmlFor="file-upload" 
              className="hover:bg-jade/20 flex h-8 sm:h-9 w-8 sm:w-9 cursor-pointer items-center justify-center rounded-full transition-colors"
            >
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <Paperclip className="text-jade-light size-4 sm:size-5" />
            </label>
            
            <Button
              variant="default"
              size="sm"
              type="button"
              onClick={handleButtonClick}
              className={cn(
                "h-8 sm:h-9 w-8 sm:w-9 rounded-full text-white shadow-md shadow-jade/20 transition-all hover:shadow-jade/30 p-0",
                hasText ? "bg-jade hover:bg-jade-light" : "bg-gray-700 hover:bg-gray-600",
                isLoading && "opacity-60 cursor-not-allowed"
              )}
              disabled={isLoading || !hasText}
              aria-label="Submit"
            >
              <Send className="size-4 sm:size-5" />
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;
